/**
 * MarketplaceDetailSheet — the one App-Store-style detail panel of a marketplace listing (entity-management §5.2),
 * used by the marketplace page, the Tools and Templates search results and `EntitySearchInput`.
 * Demo pages: /pricing-tables (plan card: price, highlights, one call to action) and /ui-elements/dialogs (sheet).
 * Install and fork sit inside `<Can policy="CanInstallMarketplace">`, the policy of both server routes.
 * Install acquires the listing's locked version; fork (public listings only) copies it into a new, editable entity.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, GitFork, Globe, Lock } from 'lucide-react';
import {
  forkMarketplaceListingMutation,
  installMarketplaceListingMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { MarketplaceListing } from '../../generated/api/types.gen.js';
import { zMarketplaceForkRequest } from '../../generated/api/zod.gen.js';
import { Form, FormActions, TextFormField, useZodForm } from '../form/index.js';
import { Can } from '../auth/Can.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { Sheet } from '../ui/Sheet.js';
import { useToast } from '../ui/Toast.js';
import { marketplaceEntityLabel } from '../../pages/marketplace/marketplaceLabels.js';
import { canForkListing, slugifyListingTitle } from './marketplaceDetail.helpers.js';

interface MarketplaceDetailSheetProps {
  /** The listing to show; `null` closes the sheet. */
  listing: MarketplaceListing | null;
  onClose: () => void;
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="text-heading">{children}</dd>
    </div>
  );
}

function ForkForm({ listing, onDone }: { listing: MarketplaceListing; onDone: () => void }) {
  const fork = useMutation(forkMarketplaceListingMutation());
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const form = useZodForm(zMarketplaceForkRequest, {
    defaultValues: { name: listing.title, slug: slugifyListingTitle(listing.title) },
  });
  const submit = async (body: { name: string; slug: string }) => {
    await fork.mutateAsync({ path: { id: listing.id }, body });
    // Mark every held query stale and refetch what is on screen: the fork is a new entity on some list.
    await queryClient.invalidateQueries();
    notify(`${body.name} is now yours to edit.`);
    onDone();
  };
  return (
    <Form form={form} onSubmit={submit} className="space-y-4">
      <p className="text-sm font-medium text-heading">Fork into a new, editable copy</p>
      <TextFormField name="name" label="Name" required />
      <TextFormField name="slug" label="Identifier" required description="Lowercase letters, numbers and dashes." />
      <FormActions submitLabel="Fork" onCancel={onDone} className="md:col-span-1" />
    </Form>
  );
}

export const MarketplaceDetailSheet: React.FC<MarketplaceDetailSheetProps> = ({ listing, onClose }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [forking, setForking] = useState(false);
  const install = useMutation({
    ...installMarketplaceListingMutation(),
    onSuccess: async () => {
      // The installed entity shows on lists of every kind (marketplace, tools, templates, workforce, workflows).
      await queryClient.invalidateQueries();
      notify('Installed in your organization.');
    },
    onError: (error) => notify(error.message, 'danger'),
  });

  const close = () => {
    setForking(false);
    install.reset();
    onClose();
  };

  const isFree = listing?.pricingModel === 'free' || !listing?.priceUsd;
  const canFork = listing !== null && canForkListing(listing);

  return (
    <Sheet
      open={listing !== null}
      onClose={close}
      title={listing?.title ?? 'Listing'}
      description={listing ? marketplaceEntityLabel(listing.entityType) : undefined}
      width="lg"
      footer={
        listing && (
          <Can policy="CanInstallMarketplace">
            {install.isSuccess ? (
              <div className="flex w-full items-center justify-between">
                <span className="text-sm font-medium text-success">Installed in your organization</span>
                <Button variant="secondary" onClick={close}>
                  Close
                </Button>
              </div>
            ) : (
              <div className="flex w-full items-center gap-2">
                <Button
                  className="flex-1"
                  loading={install.isPending}
                  onClick={() => install.mutate({ path: { id: listing.id } })}
                >
                  Install in my organization
                </Button>
                {canFork && !forking && (
                  <Button variant="secondary" onClick={() => setForking(true)}>
                    <GitFork size={14} aria-hidden="true" />
                    Fork
                  </Button>
                )}
              </div>
            )}
          </Can>
        )
      }
    >
      {listing && (
        <div className="space-y-6">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-3xl font-semibold text-heading">{isFree ? 'Free' : `$${listing.priceUsd}`}</p>
            <Badge variant="neutral" icon={canFork ? <Globe size={12} /> : <Lock size={12} />}>
              {canFork ? 'Public' : 'Private'}
            </Badge>
          </div>

          {listing.description && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-heading">About</h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-body">{listing.description}</p>
            </section>
          )}

          <section>
            <h3 className="mb-2 text-sm font-semibold text-heading">Highlights</h3>
            <ul className="space-y-2 text-sm text-body">
              <li className="flex items-center gap-2">
                <Check size={14} className="text-success" aria-hidden="true" />
                {canFork
                  ? 'Readable and forkable by any organization'
                  : 'Runs in your organization; its content stays private'}
              </li>
              <li className="flex items-center gap-2">
                <Check size={14} className="text-success" aria-hidden="true" />
                Installs the version published now; you decide when to update
              </li>
            </ul>
          </section>

          {listing.tags && listing.tags.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-heading">Tags</h3>
              <div className="flex flex-wrap gap-1.5">
                {listing.tags.map((tag) => (
                  <Badge key={tag} variant="neutral">
                    {tag}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          <dl className="divide-y divide-card-border rounded-lg border border-card-border px-4">
            <Detail label="Type">{marketplaceEntityLabel(listing.entityType)}</Detail>
            {listing.category && <Detail label="Category">{listing.category}</Detail>}
            {listing.publishedAt && (
              <Detail label="Published">{new Date(listing.publishedAt).toLocaleDateString()}</Detail>
            )}
          </dl>

          {forking && (
            <Can policy="CanInstallMarketplace">
              <ForkForm key={listing.id} listing={listing} onDone={close} />
            </Can>
          )}
        </div>
      )}
    </Sheet>
  );
};
